import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Row, Col, Input, InputGroup, Spinner } from "reactstrap";
import Select from 'react-select'; // Import react-select
import { CheckUserExistence } from "../../../slices/thunks"; // Adjust path as needed

const AddEnrollUserForm = ({ labels, formikProps }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();


  const { EnrollStudents, Country: countriesList, Genders: genders, userTypesList, Courses: courses } = useSelector((state) => ({
      EnrollStudents: state.EnrollStudents.enrollStudents,
      Country: state.Module.country,
      Genders: state.Module.genders,
      userTypesList: state.UserType.userTypesList,
      Courses:  state.Courses.courses,
  }));
  
  
  // Destructure from formikProps for easier access
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formikProps;

  // State for email and mobile existence check
  const [emailCheckStatus, setEmailCheckStatus] = useState({ loading: false, available: null, checkedValue: '', error: null });
  const [mobileCheckStatus, setMobileCheckStatus] = useState({ loading: false, available: null, checkedValue: '', error: null });
  const [isNewStudentSelected, setIsNewStudentSelected] = useState(values.UserId === "NewStudent" || !values.UserId);

  const handleExistenceCheck = async (fieldName, value) => {
    let setCheckStatus, payload;

    if (fieldName === "Email") {
      if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || value === emailCheckStatus.checkedValue) return;
      setCheckStatus = setEmailCheckStatus;
      payload = { email: value };
    } else if (fieldName === "Mobile") {
      if (!value) return;
      setCheckStatus = setMobileCheckStatus;
      payload = { mobile: value };
    } else {
      return;
    }

    setCheckStatus({ loading: true, available: null, checkedValue: value, error: null });
    try {
      const result = await dispatch(CheckUserExistence(payload)).unwrap();
      if (result.exists) {
        setCheckStatus({
          loading: false,
          available: false, 
          checkedValue: value,
          error: t(result.message) || t(`${fieldName}AlreadyInUse`) 
        });
      } else {
        // Item does not exist, so it's available
        setCheckStatus({ loading: false, available: true, checkedValue: value, error: null });
      }
    } catch (rejectedValueOrSerializedError) {
      // Handle errors from the API call itself (e.g., network error, or thunk rejected)
      const errorMessage = rejectedValueOrSerializedError?.error ||
                           rejectedValueOrSerializedError?.message ||
                           t('existenceCheckFailed');
      setCheckStatus({ loading: false, available: null, checkedValue: value, error: errorMessage });
    }
  };

  const options = useMemo(() => {
        if (Array.isArray(EnrollStudents)) {
            return [  
                {
                    UserId: "NewStudent", // Ensure this matches the property name in formik values
                    label: t('New Student'),
                    FirstName: "",
                    LastName: "",
                    CountryCodeID: values.CountryCodeID || "", // Default to existing or empty
                    Mobile: "",
                    Email: "",
                    GenderID: "",
                    // RoleId: "" // RoleId for student is usually fixed, adjust if needed
                },
                ...EnrollStudents.map((student) => ({
                UserId: student.UserId,
                label: `${student?.FullName} - (${student.Mobile}) - (${student.Email})`,
                FirstName: student.FirstName,
                LastName: student.LastName,
                CountryCodeID: student.CountryCodeID,
                Mobile: student.Mobile,
                Email: student.Email,
                GenderID: student.GenderID,
                UserTypeID: student.UserTypeID,
            }))];
        }
        return [];
  }, [EnrollStudents, t, values.CountryCodeID]);

  const handleStudentSelection = (selectedOption) => {
    if (selectedOption) {
      setFieldValue("UserId", selectedOption.UserId);
      if (selectedOption.UserId === "NewStudent") {
        setIsNewStudentSelected(true);
        // Clear other fields or set to default for a new student
        setFieldValue("FirstName", "");
        setFieldValue("LastName", "");
        // setFieldValue("CountryCodeID", values.CountryCodeID || ""); // Keep selected country code or reset
        setFieldValue("Mobile", "");
        setFieldValue("Email", "");
        setFieldValue("GenderID", "");
      } else {
        setIsNewStudentSelected(false);
        // Populate fields with selected student's data
        Object.keys(selectedOption).forEach(key => {
          if (key !== 'label' && key !== 'value') { // 'value' was UserId, already set
            setFieldValue(key, selectedOption[key] || "");
          }
        });
      }
    } else {
      // Handle clear selection if needed
      setIsNewStudentSelected(true); // Default to new student fields being enabled
      setFieldValue("UserId", "");
    }
  };

  const renderField = (item) => {
    const fieldValue = values[item.name] ?? "";

    switch (item.fieldName) {
      case "text-box":
      case "email-box":
        const isEmail = item.fieldName === "email-box";
        const currentCheckStatus = isEmail ? emailCheckStatus : null;

        return (
          <InputGroup>
            <Input
              type={"text"}
              inputMode={isEmail ? "email" : "text"}
              id={item.name}
              name={item.name}
              placeholder={t(item.placeholder || item.labelName)}
              value={fieldValue}
              onChange={handleChange}
              onBlur={(e) => {
                handleBlur(e); // Formik's blur
                if (isEmail) {
                  handleExistenceCheck(item.name, e.target.value);
                }
              }}
              className="form-control"
              invalid={touched[item.name] && !!errors[item.name] || (isEmail && currentCheckStatus?.available === false && currentCheckStatus?.checkedValue === fieldValue)}
              required={item.required}
              disabled={!isNewStudentSelected && values.UserId !== "NewStudent"} 
            />
            {isEmail && (
              <div className="input-group-append" style={{ display: 'flex', alignItems: 'center', marginLeft: '5px', minWidth: '25px' }}>
                {currentCheckStatus?.loading && <Spinner size="sm" color="primary" />}
                {!currentCheckStatus?.loading && currentCheckStatus?.available === true && currentCheckStatus?.checkedValue === fieldValue && !errors[item.name] && (
                  <span style={{ color: 'green', fontSize: '1.2em' }}>✓</span>
                )}
                {!currentCheckStatus?.loading && currentCheckStatus?.available === false && currentCheckStatus?.checkedValue === fieldValue && !errors[item.name] && (
                  <span style={{ color: 'red', fontSize: '1.2em' }}>✗</span>
                )}
              </div>
            )}
          </InputGroup>
        );

      case "number":
        // This case seems to be for a generic number.
        // If this 'number' type is for mobile, it should ideally use CountryCodeID from formik values
        const countryForNumberField = values.CountryCodeID ? countriesList.find((c) => c.ID === parseInt(values.CountryCodeID)) : null;
        const maxLength = countryForNumberField?.NumbersLimit || 15;

        return (
          <Input
            type="tel"
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={handleChange} 
            onBlur={handleBlur}
            className="form-control"
            maxLength={maxLength}
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
            disabled={!isNewStudentSelected && values.UserId !== "NewStudent"}
          />
        );

      case "mobile-box":
          const selectedCountryForMobile = countriesList.find((c) => c.ID === parseInt(values.CountryCodeID));
          const mobileMaxLength = selectedCountryForMobile?.NumbersLimit ?? 10;
          const mobileInputName = item.name; 
          const mobileCurrentCheckStatus = mobileCheckStatus;
          const mobileInputValue = values[mobileInputName] ?? "";
  
          // Render Country Code Select and its error separately
          return (
            <Row>
              <Col sm={4}>
                  <label htmlFor="CountryCodeID" className="col-form-label">
                    {t("Country Code")}
                    {item.required && <span className="text-danger"> *</span>} {/* Assuming country code is required if mobile is */}
                  </label>
                  <Input
                    type="select"
                    id="CountryCodeID"
                    name="CountryCodeID"
                    value={values.CountryCodeID || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form-control"
                    disabled={!isNewStudentSelected && values.UserId !== "NewStudent"}
                    invalid={touched.CountryCodeID && !!errors.CountryCodeID} // Use invalid prop for styling
                  >
                    <option value="">{t("Select Country")}</option>
                  {countriesList.map((c) => (
                    <option key={c.ID} value={c.ID}>
                      {c.code} {i18n.language === "en" ? c.en : c.ar}
                    </option>
                  ))}
                  </Input>
                {/* Display error specifically for CountryCodeID */}
                {touched.CountryCodeID && errors.CountryCodeID && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{errors.CountryCodeID}</div>
                )}
              </Col>
              {/* Render Mobile Number Input and its error */}
              <Col sm={8}>
                <label htmlFor="CountryCodeID" className="col-form-label">
                  {t(item.labelName)}
                  {item.required && <span className="text-danger"> *</span>} {/* Assuming country code is required if mobile is */}
                </label>
                <InputGroup>
                  <Input
                    type="text" // Changed to text to allow manual non-numeric input if needed, validation handles numeric
                    id={mobileInputName}
                    name={mobileInputName}
                    placeholder={t(item.placeholder || item.labelName)}
                    value={mobileInputValue} 
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, ""); // Allow only digits
                      setFieldValue(mobileInputName, numericValue); 
                    }}
                    onBlur={(e) => {
                      handleBlur(e); // Formik's blur
                      handleExistenceCheck(mobileInputName, e.target.value);
                    }}
                    maxLength={mobileMaxLength}
                    className="form-control"
                    invalid={touched[mobileInputName] && !!errors[mobileInputName] || (mobileCurrentCheckStatus?.available === false && mobileCurrentCheckStatus?.checkedValue === mobileInputValue)}
                    disabled={!isNewStudentSelected && values.UserId !== "NewStudent"}
                    required={item.required}
                  />
                  <div className="input-group-append" style={{ display: 'flex', alignItems: 'center', marginLeft: '5px', minWidth: '25px' }}>
                    {mobileCurrentCheckStatus?.loading && <Spinner size="sm" color="primary" />}
                    {!mobileCurrentCheckStatus?.loading && mobileCurrentCheckStatus?.available === true && mobileCurrentCheckStatus?.checkedValue === mobileInputValue && !errors[mobileInputName] && (
                      <span style={{ color: 'green', fontSize: '1.2em' }}>✓</span>
                    )}
                    {!mobileCurrentCheckStatus?.loading && mobileCurrentCheckStatus?.available === false && mobileCurrentCheckStatus?.checkedValue === mobileInputValue && !errors[mobileInputName] && (
                      <span style={{ color: 'red', fontSize: '1.2em' }}>✗</span>
                    )}
                  </div>
                </InputGroup>
                {/* Display error specifically for the mobile number field */}
                {touched[mobileInputName] && errors[mobileInputName] && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{errors[mobileInputName]}</div>
                )}
                {!errors[mobileInputName] && mobileCurrentCheckStatus?.error && (
                  <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(mobileCurrentCheckStatus.error)}</div>
                )}
              </Col>
            </Row>
          );
      
      case 'search-box':
          return (
            <> {/* Removed redundant div.mb-3, outer loop handles it */}
              <label htmlFor={item?.labelName} className="col-form-label">{t(item?.labelName)}{item.required && <small className="text-danger"> *</small>}</label>
                <Select
                    id={item.name}
                    name={item.name}
                    value={options.find(option => option.UserId === values[item.name]) || null} 
                    onChange={handleStudentSelection}
                    onBlur={handleBlur} 
                    options={options}
                    getOptionValue={(option) => option.UserId} 
                    placeholder={t("Search by Name, Email, Mobile")}
                    isClearable
                    classNamePrefix="select2-selection"
                    filterOption={(option, inputValue) => {
                        const { label } = option; 
                        const studentData = option;
                        const searchString = inputValue.toLowerCase();

                        return (
                            label.toLowerCase().includes(searchString) ||
                            (studentData.Mobile && studentData.Mobile.toLowerCase().includes(searchString)) ||
                            (studentData.Email && studentData.Email.toLowerCase().includes(searchString))
                        );
                    }}
                />
                {touched[item.name] && errors[item.name] && ( // Display validation error for UserId
                    <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{errors[item.name]}</div>
                )}
            </>
          );

      case "country-code":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            disabled={!isNewStudentSelected && values.UserId !== "NewStudent"}
            invalid={touched[item.name] && !!errors[item.name]}
          >
            {countriesList.map((c) => (
              <option key={c.ID} value={c.ID}>
                {`${c?.[i18n.language]} (${c.code})`}
              </option>
            ))}
          </Input>
        );

      case "user-type":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            disabled={!isNewStudentSelected && values.UserId !== "NewStudent"} // UserType might be fixed for students
            invalid={touched[item.name] && !!errors[item.name]}
          >
            <option value="">{t("Select User Type")}</option>
            {userTypesList.map((type) => (
              <option key={type.ID} value={type.ID}>
                {i18n.language === "ar" ? type.UserTypeAr : type.UserTypeEn}
              </option>
            ))}
          </Input>
        );

      case "gender-dropdown":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            disabled={!isNewStudentSelected && values.UserId !== "NewStudent"}
            invalid={touched[item.name] && !!errors[item.name]}
          >
            <option value="">{t("SelectGender")}</option>
            {genders.map((g) => (
              <option key={g.ID} value={g.ID}>
                {g[i18n.language]}
              </option>
            ))}
          </Input>
        );

      case "course-dropdown":
        return (
          <Input
            type="select"
            id={item.name}
            name={item.name}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            // disabled={!isNewStudentSelected && values.UserId !== "NewStudent"} // Course is likely always selectable
            invalid={touched[item.name] && !!errors[item.name]}
          >
            <option value="">{t("Select Course")}</option>
            {courses.map((course) => (
              <option key={course.ID} value={course.ID}>
                {i18n.language === "en" ? course.CourseNameEn : course.CourseNameAr}
              </option>
            ))}
          </Input>
        );

      case "Branches":
        const selectedBranchIds = Array.isArray(values[item.name]) ? values[item.name] : [];
        const selectedBranchObjects = branches.filter((branch) =>
            selectedBranchIds.includes(branch.BranchId)
        );

        return (
          <Multiselect
            options={branches}
            selectedValues={selectedBranchObjects}
            displayValue={i18n.language === "ar" ? "BranchNameAr" : "BranchNameEn"}
            onSelect={(selectedList, selectedItem) => {
              const newValues = selectedList.map((b) => b.BranchId);
              setFieldValue(item.name, newValues);
              
            }}
            onRemove={(selectedList, removedItem) => {
              const newValues = selectedList.map((b) => b.BranchId);
              setFieldValue(item.name, newValues);
            }}
            placeholder={t("SelectBranches")}
            className="form-control"
            style={{ multiselectContainer: { width: "100%" } }}
            // disabled={!isNewStudentSelected && values.UserId !== "NewStudent"}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Row>
      {labels.map((item) => {
        const isEmail = item.fieldName === "email-box";
        const isMobileBox = item.fieldName === "mobile-box";
        const isSearchBox = item.fieldName === "search-box";
        const currentCheckStatus = isEmail ? emailCheckStatus : (isMobileBox ? mobileCheckStatus : null);

        return (
          <Col sm={6} key={item.name}>
            <div className="mb-3">
              {/* For mobile-box and search-box, labels are handled inside their renderField or are part of the component itself */}
              {!isMobileBox && !isSearchBox && (
                <label htmlFor={item.name} className="col-form-label">
                  {t(item.labelName)}
                  {item.required && <span className="text-danger"> *</span>}
                </label>
              )} 
              {/* Render the field */}
              {renderField(item)}
              {/* Display error for the main field */}
              {!isMobileBox && !isSearchBox && touched[item.name] && errors[item.name] && (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{errors[item.name]}</div>
              )}
              {/* Display API check error if not already shown by Formik */}
              {isEmail && !errors[item.name] && currentCheckStatus?.error && (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(currentCheckStatus.error)}</div>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

export default AddEnrollUserForm;
